import { useEffect } from 'react';
import { ROUTE_PATHS } from 'routes';
import { Analytics } from 'utils/analytics';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';
import { useLocalStorage, useModal } from 'hooks';
import { setTokenData } from 'store/site-actions';
import { BasicButton, Link } from 'components/common/button';
import { color, isMobile } from 'constants/styled-theme';
import { changeTab } from 'components/active-auctions/actions';
import { useTokenData } from '../hooks';

const ButtonsContainer = styled.div`
  display: flex;
  color: #7d1f49;
  flex-direction: ${p => (p.isLoggedIn ? 'column' : 'row')};
  span,
  b {
    font-size: 18px;
    text-transform: capitalize;
  }

  @media (min-width: 1024px) and (max-width: 1300px) {
    span,
    b {
      font-size: 13px;
    }
  }
  @media (max-width: ${p => p.theme.mobile}) {
    position: absolute;
    right: 4px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    span,
    b {
      text-align: left;
      font-size: 15px;
      align-self: flex-start;
    }
  }
`;

const logoutBtnStyle = css`
  font-size: 16px;
  border: none;
  text-transform: capitalize;
  background-color: transparent;
  color: ${color('gray100')};
  text-align: left;
  justify-content: flex-start;
  padding: 0;
  :hover {
    color: ${color('gray90')};
    font-family: ${props => props.theme.fonts.semiBold};
  }
  @media (min-width: 1024px) and (max-width: 1300px) {
    font-size: 13px;
    height: 25px;
    max-height: 25px;
  }
  @media (max-width: ${p => p.theme.mobile}) {
    width: 78px;
    max-width: 78px;
    height: 22px;
    max-height: 22px;
    font-size: 12px;
    text-align: right;
    font-family: ${props => props.theme.fonts.regular};
    &:hover {
      color: ${color('gray90')};
      font-family: ${props => props.theme.fonts.regular};
    }
  }
`;

const buttonStyle = css`
  margin: 10px;
  font-size: 16px;
  color: ${color('activeMenuColor')};
  background-color: ${color('gray0')};
  border: solid 1px ${color('activeMenuColor')};
  border-radius: 3px;
  display: flex;
  justify-content: center;
  align-items: center;
  max-height: 33px;
  font-family: ${props => props.theme.fonts.semiBold};
  height: 100%;
  min-width: 120px;
  white-space: nowrap;
  text-transform: uppercase;
  :hover {
    background: ${color('activeMenuColor')};
    color: ${color('gray0')};
    border: solid 2px ${color('activeMenuColor')};
  }

  @media (min-width: 1024px) and (max-width: 1300px) {
    font-size: 13px;
    height: 25px;
    max-height: 25px;
    padding: 0;
  }

  @media (max-width: ${p => p.theme.mobile}) {
    font-size: 12px;
    margin: 2px 4px;
    text-transform: none;
    font-family: ${props => props.theme.fonts.semiBold};
    width: 78px;
    max-width: 78px;
    border-radius: 3px;
    height: 20px;
    max-height: 20px;
    min-width: 78px;
    padding: 0;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;
const LoginOutButton = styled(BasicButton)`
  ${p => (p.isLoggedIn ? logoutBtnStyle : buttonStyle)};
  width: 85px;
  max-height: 33px;
  height: 33px;
  min-width: auto;
  @media (max-width: ${p => p.theme.mobile}) {
    width: 78px;
    max-width: 78px;
    height: 22px;
    max-height: 22px;
    font-size: 12px;
    text-align: right;
  }
`;
const RegisterButton = styled(Link)`
  ${buttonStyle};
  height: 31px;
  padding: 0;
  width: 104px;
  @media (max-width: ${p => p.theme.mobile}) {
    width: 78px;
    max-width: 78px;
    height: 22px;
    max-height: 22px;
    font-size: 12px;
  }
`;

export const LoginButton = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { removeStorage, setStorage } = useLocalStorage();
  const { modal } = useModal(null);
  const { givenName, nickName, accountId, isAuth } = useTokenData();
  const firstName = givenName || nickName;
  const masterId = accountId || '';
  const isCatalogScreen = CONFIG.AUCTION_TYPE === 'collection';

  const onClickHandler = () => {
    removeStorage('userData');
    if (isCatalogScreen && !isAuth) {
      setStorage('fromCatalogScreen', 'true');
    }
    dispatch(changeTab());
    if (!isAuth) return history.push(ROUTE_PATHS.LOGIN);
    removeStorage('token');
    dispatch(setTokenData(null));
    history.push(
      // isCatalogScreen ? ROUTE_PATHS.ACTIVE_CATALOG_AUCTIONS : ROUTE_PATHS.ACTIVE_AUCTIONS
      ROUTE_PATHS.ACTIVE_AUCTIONS
    );
  };

  useEffect(() => {
    if (![ROUTE_PATHS.ACTIVE_AUCTIONS, ROUTE_PATHS.PREVIEW_AUCTIONS].includes(location.pathname)) {
      Analytics.track('pageview', {
        ActionName: 'Page View',
        account_id: masterId,
        url: location.pathname + location.search,
        title: document.title
      });
    }
  }, [location.pathname]);

  return (
    <>
      {modal}
      <ButtonsContainer isLoggedIn={isAuth}>
        {firstName && !isMobile && (
          <span>
            <b>
              Hello, {firstName} {masterId ? `(${masterId})` : ''}
            </b>
          </span>
        )}
        {firstName && isMobile && (
          <>
            <span>
              <b>Hello,</b>
            </span>
            <span>
              <b>{firstName}!</b>
            </span>
          </>
        )}
        <LoginOutButton onClick={onClickHandler} white isLoggedIn={isAuth}>
          {isAuth ? 'Log Out' : 'Log In'}
        </LoginOutButton>
        {!isAuth && (
          <RegisterButton white href={`${CONFIG.REGISTRATION_URL}`} target={'_blank'}>
            Register
          </RegisterButton>
        )}
      </ButtonsContainer>
    </>
  );
};
